import { gettext } from '@eventbrite/i18n';
import { LoadingSkeleton } from '@eventbrite/loading-skeleton';
import { Tabs, TabsItem, Typography } from '@eventbrite/marmalade';
import { useQuery } from '@tanstack/react-query';
import { cloneDeep } from 'lodash';
import React, { useContext, useState } from 'react';
import {
    OrganicEventSearchApiPayload,
    searchOrganicEvents,
} from '../../../../api/searchOrganicEvents';
import { NEIGHBOURHOOD } from '../../../../constants/neighbourhoodMapping';
import useFetchCachedEventIds from '../../../../hooks/useFetchCachedEventIds';
import { BucketsContent, ThingsToDoLocationData } from '../../../../types';
import { transformResponse } from '../../../categoryBrowse/hooks/queries/events';
import { BrowseDataContext } from '../../context';
import { BrowseBucketContainer } from '../BrowseBucket';
import styles from './Neighbourhood.module.scss';

type NeighbourhoodContextData = {
    buckets: BucketsContent;
    removeEvent: (bucketKey: string, eventId: string) => void;
};

interface NeighbourhoodProps {
    locationData: ThingsToDoLocationData;
}

const CITYBROWSE_NEIGHBOURHOOD = 'CityBrowse - Neighbourhood';
const EVENT_EXPANSIONS = [
    'primary_venue',
    'image',
    'ticket_availability',
    'saves',
    'event_sales_status',
    'primary_organizer',
    'public_collections',
];

const _getInitialData = ({ buckets }: { buckets: BucketsContent }) => {
    return buckets
        ? buckets.filter((bucket) => bucket.keyMin === 'cnl')[0] || null
        : null;
};

export const NeighbourhoodContainer = (props: NeighbourhoodProps) => {
    const { buckets, removeEvent }: NeighbourhoodContextData =
        useContext(BrowseDataContext);
    const { locationData } = props;

    const neighbourhoodData = NEIGHBOURHOOD[locationData.placeId || ''];
    const initialData = cloneDeep(_getInitialData({ buckets }));
    // State to manage the active tab and tab data
    const [activeTab, setActiveTab] = useState(0);
    const eventIds = useFetchCachedEventIds({
        queryKeyPrefix: 'citybrowse-neighbourhood',
        locationData: locationData,
        tabs: [0, 1, 2],
    });

    //Data fetching function using React Query
    const query: OrganicEventSearchApiPayload = {
        browse_surface: 'browse',
        event_search: {
            page_size: 4,
            dates: ['current_future'],
            point_radius: neighbourhoodData
                ? neighbourhoodData[activeTab].point_radius
                : {},
            related_events: { event_ids: eventIds, exclude_event_ids: true },
        },
        'expand.destination_event': EVENT_EXPANSIONS,
    };

    const eventData = useQuery(
        ['citybrowse-neighbourhood', locationData, activeTab],
        () => searchOrganicEvents(query),
        {
            select: transformResponse,
            refetchOnMount: false,
            enabled: Boolean(neighbourhoodData),
            staleTime: 300000, //5 minutes: 5 * 60 * 1000
        },
    );

    const tabData = initialData || null;
    if (activeTab != 0 && eventData.data && eventData.data.events) {
        tabData.content = eventData.data.events;
    }

    if (!tabData) {
        return null;
    }
    tabData.name = null;
    const tabs = [
        { id: 0, title: neighbourhoodData[0].name },
        { id: 1, title: neighbourhoodData[1].name },
        { id: 2, title: neighbourhoodData[2].name },
    ];
    // Event handler to set the active tab and fetch tab data
    const handleTabClick = (tabId: number) => {
        setActiveTab(tabId);
    };

    return (
        <div className={styles.root}>
            <Typography as="h2" className={styles.title} variant="heading-sm">
                {gettext('Explore by neighborhood')}
            </Typography>

            <Tabs
                aria-label="neighbourhood-tabs"
                className={styles.tabs}
                defaultValue={activeTab.toString()}
                onValueChange={(value) => handleTabClick(parseInt(value))}
            >
                {tabs.map((tab) => (
                    <TabsItem
                        key={tab.id}
                        className={`${styles.tab}
                            ${activeTab === tab.id ? styles.active : ''}
                        `}
                        value={tab.id.toString()}
                    >
                        {tab.title}
                    </TabsItem>
                ))}
            </Tabs>

            {/* Render content for active tab */}
            <div
                className={styles.content}
                data-heap-id={CITYBROWSE_NEIGHBOURHOOD}
                id={`radix-3-content-${activeTab}`}
            >
                {activeTab != 0 && eventData.isLoading ? (
                    <LoadingSkeleton height={'336px'} />
                ) : (
                    <>
                        <BrowseBucketContainer
                            bucketWrapperClasses={`content-bucket eds-l-mar-top-5 eds-l-sm-mar-top-16 eds-l-sn-mar-top-16`}
                            bucketKey={tabData.key}
                            onRemoveEvent={removeEvent}
                            {...tabData}
                        />
                    </>
                )}
            </div>
        </div>
    );
};
